/* eslint-disable max-lines-per-function */
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getAllEntities } from '@adornis/baseql/operations/mongo.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ConvertConfig, FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { parseBoolean, parseDate } from '@adornis/import-export/db/default-parse-functions.js';
import { serializeDate } from '@adornis/import-export/db/default-serialize-functions.js';
import { AttendanceAPI, AttendanceOverviewResult } from '@chancenwerk/attendance/api/AttendanceAPI.js';
import { newOperationCache } from '@chancenwerk/db/utils/cache.js';
import { DocumentationAPI } from '@chancenwerk/documentations/api/DocumentationAPI.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { BuT, DayAvailable, Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import * as isoCountry from '@hotosm/iso-countries-languages';
import { DateTime, Settings } from 'luxon';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { ChancenUserActivityAnalytics } from '../analytics/ChancenUserActivityAnalytics.js';
import { CourseDigital } from '../courses/CourseDigital.js';
import { SchoolSubject } from '../enums/enums.js';
import { PageLoadEventLog } from '../eventlogs/PageLoadEventlog.js';
import { CourseFilter } from '../filter/CourseFilter.js';
import { SemanticRelationship } from '../semantics/SemanticRelationship.js';

Settings.defaultLocale = 'de';

const htmlCleanPattern = /(<\/?p>)|(<\/?br>)|\n/g;

function cleanHTMLFieldForExport(htmlContent?: string) {
  if (!htmlContent) return '';
  return htmlContent
    .replaceAll(htmlCleanPattern, ' ')
    .replaceAll(/&gt;/g, '>')
    .replaceAll(/&nbsp;/g, ' ')
    .trim();
}

function getActiveBuTFromStudent(student: PartialEntityData<Student>, butYear: string): PartialEntityData<BuT> {
  for (let but of student.buts ?? []) {
    const butCon = constructValue(but) as BuT;
    if (butCon.year === butYear) return but;
  }
  return new BuT({}).toObject();
}

const activeFieldBut =
  (field: string, butYear: string, parsingFunction?: any) =>
  (value: any | undefined, data: PartialEntityData<Student>, i: number) => {
    const activeBuT = getActiveBuTFromStudent(data, butYear);
    const valueFromBuT = activeBuT[field];
    if (parsingFunction) return parsingFunction(valueFromBuT);
    return valueFromBuT;
  };

@Entity()
export class StudentConvertConfig extends ConvertConfig<Student> {
  public static override _class = 'StudentConvertConfig';

  private static _butToFieldMappings(butKey: string, butYear: string, inputPrefix: string): FieldMappingEntry[] {
    return [
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Berechtigung' },
        undefined,
        activeFieldBut('BuTBerechtigung', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Unterlagen_Eltern' },
        wrappedParseDate(inputPrefix + 'Unterlagen_Eltern'),

        activeFieldBut('BuTUnterlagenEltern', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Grundantrag_erhalten' },
        wrappedParseDate(inputPrefix + 'Grundantrag_erhalten'),
        activeFieldBut('grundantragErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Leistungsbescheid_erhalten' },
        wrappedParseDate(inputPrefix + 'Leistungsbescheid_erhalten'),
        activeFieldBut('leistungsbescheidErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Zeugnis_erhalten' },
        wrappedParseDate(inputPrefix + 'Zeugnis_erhalten'),
        activeFieldBut('zeugnisErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Kind_nimmt_nicht_teil_Stand' },
        wrappedParseDate(inputPrefix + 'Kind_nimmt_nicht_teil_Stand'),
        activeFieldBut('kindNimmtNichtTeilStand', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        {
          output: butKey,
          input: inputPrefix + 'Verschwiegenheitsbogen_erhalten',
        },
        wrappedParseDate(inputPrefix + 'Verschwiegenheitsbogen_erhalten'),
        activeFieldBut('verschwiegenheitsbogenErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Unterlagen_vollstaendig' },
        wrappedParseDate(inputPrefix + 'BuT-Unterlagen_vollstaendig'),
        activeFieldBut('BuTUnterlagenVollstaendig', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Elternkontakt' },
        undefined,
        activeFieldBut('elternkontakt', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Elternkontakt_fehlgeschlagen' },
        wrappedParseDate(inputPrefix + 'Elternkontakt_fehlgeschlagen'),
        activeFieldBut('elternkontaktFehlgeschlagen', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        {
          output: butKey,
          input: inputPrefix + 'Anlass_Kommentar_Elternkontakt',
        },
        undefined,
        activeFieldBut('anlassKommentarElternkontakt', butYear, text => {
          return !text ? '' : cleanHTMLFieldForExport(text);
        }),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Zusatzbogen_Schule_versendet' },
        wrappedParseDate(inputPrefix + 'Zusatzbogen_Schule_versendet'),
        activeFieldBut('zusatzbogenSchuleVersendet', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Zusatzbogen_Schule_erhalten' },
        wrappedParseDate(inputPrefix + 'Zusatzbogen_Schule_erhalten'),
        activeFieldBut('zusatzbogenSchuleErhalten', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        {
          output: butKey,
          input: inputPrefix + 'Vereinbarungsnotitz_Schule',
        },
        undefined,
        activeFieldBut('vereinbarungsnotitzSchule', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Leistungstraeger' },
        undefined,
        activeFieldBut('leistungstraeger', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Antragsteller' },
        undefined,
        activeFieldBut('antragsteller', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Antrag_teilweise_versendet' },
        wrappedParseDate(inputPrefix + 'Antrag_teilweise_versendet'),
        activeFieldBut('antragTeilweiseVersendet', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Antrag_vollstaendig_versendet' },
        wrappedParseDate(inputPrefix + 'Antrag_vollstaendig_versendet'),
        activeFieldBut('antragVollstaendigVersendet', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        {
          output: butKey,
          input: inputPrefix + 'Vereinbarungsnotiz_Amt',
        },
        undefined,
        activeFieldBut('vereinbarungsnotitzAmt', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Aktenzeichen' },
        undefined,
        activeFieldBut('aktenzeichen', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Bewilligung_ab' },
        wrappedParseDate(inputPrefix + 'Bewilligung_ab'),
        activeFieldBut('BuTBewilligungAb', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Bewilligung_bis' },
        wrappedParseDate(inputPrefix + 'Bewilligung_bis'),
        activeFieldBut('BuTBewilligungBis', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Kommentar' },
        undefined,
        activeFieldBut('kommentarBuT', butYear, text => {
          return !text ? '' : cleanHTMLFieldForExport(text);
        }),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Kommentar_Schuko' },
        undefined,
        activeFieldBut('anlassKommentarBuTSchuko', butYear, text => {
          return !text ? '' : cleanHTMLFieldForExport(text);
        }),
      ),

      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Massnahmen_einstellen_bis' },
        wrappedParseDate(inputPrefix + 'Massnahmen_einstellen_bis'),
        activeFieldBut('BuTMassnahmenEinstellenBis', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        {
          output: butKey,
          input: inputPrefix + 'Leistungsbescheid_gueltig_bis',
        },
        wrappedParseDate(inputPrefix + 'Leistungsbescheid_gueltig_bis'),
        activeFieldBut('leistungsbescheidBerlinPassGueltigBis', butYear, serializeDate()),
      ),

      ...this._getFächer(inputPrefix, butYear),

      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Zugewiesene_Person_Elternkontakt' },
        undefined,
        activeFieldBut('assignedPersonForParentalContact', butYear),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'Kommentar_Abrechnung' },
        undefined,
        activeFieldBut('kommentarAbrechnung', butYear, cleanHTMLFieldForExport),
      ),
      new FieldMappingEntry(
        {
          output: butKey,
          input: inputPrefix + 'Abgerechnet_bis',
        },
        wrappedParseDate(inputPrefix + 'Abgerechnet_bis'),
        activeFieldBut('abgerechnetBis', butYear, serializeDate()),
      ),
      new FieldMappingEntry(
        { output: butKey, input: inputPrefix + 'berechtigt_durch' },
        undefined,
        activeFieldBut('BuTBerechtigungDurch', butYear),
      ),
    ];
  }

  private static _getFächer(inputPrefix: string, butYear: string) {
    const subjects = [SchoolSubject.DEUTSCH, SchoolSubject.ENGLISCH, SchoolSubject.MATHE];
    return [
      ...subjects.flatMap(s => [
        new FieldMappingEntry({ output: null, input: inputPrefix + 'Bewilligt_' + s }, undefined, (v, data, i) => {
          const but = getActiveBuTFromStudent(data, butYear);
          if (!but) return 'Nein';
          return (but.faecher ?? []).filter(f => f.subject === s).at(0)?.approved ? 'Ja' : 'Nein';
        }),
        new FieldMappingEntry(
          { output: null, input: inputPrefix + 'Bewilligte_Stunden_' + s },
          undefined,
          (v, data, i) => {
            const but = getActiveBuTFromStudent(data, butYear);
            if (!but) return '';
            const result = ((but.faecher ?? []).filter(f => f.subject === s).at(0)?.bewilligteStunden ?? '').toString();
            return (result === '0' ? '' : result).replace('.', ',');
          },
        ),
        new FieldMappingEntry(
          { output: null, input: inputPrefix + 'Berechnete_Stunden_' + s },
          undefined,
          (v, data, i) => {
            const but = getActiveBuTFromStudent(data, butYear);
            if (!but) return '';
            const result = ((but.faecher ?? []).filter(f => f.subject === s).at(0)?.berechneteStunden ?? '').toString();
            return (result === '0' ? '' : result).replace('.', ',');
          },
        ),
      ]),

      new FieldMappingEntry({ output: null, input: inputPrefix + 'Bewilligt_Freitext' }, undefined, (v, data, i) => {
        const but = getActiveBuTFromStudent(data, butYear);
        if (!but) return '';
        return (but.faecher ?? [])
          .filter(f => !subjects.includes(f.subject))
          .map(f => f.subject)
          .join(', ');
      }),
      new FieldMappingEntry(
        { output: null, input: inputPrefix + 'Bewilligte_Stunden_Freitext' },
        undefined,
        (v, data, i) => {
          const but = getActiveBuTFromStudent(data, butYear);
          if (!but) return '';
          return (but.faecher ?? [])
            .filter(f => !subjects.includes(f.subject))
            .map(f => f.bewilligteStunden ?? '0')
            .join(', ');
        },
      ),
      new FieldMappingEntry(
        { output: null, input: inputPrefix + 'Berechnete_Stunden_Freitext' },
        undefined,
        (v, data, i) => {
          const but = getActiveBuTFromStudent(data, butYear);
          if (!but) return '';
          return (but.faecher ?? [])
            .filter(f => !subjects.includes(f.subject))
            .map(f => f.berechneteStunden ?? '0')
            .join(', ');
        },
      ),
    ];
  }

  constructor() {
    let kpfCached = false;
    const allKPFs = getAllEntities(KPF)({
      _id: 1,
      _class: 1,
      school: { kuerzel: 1 },
      produkt: { kuerzel: 1 },
      kuerzel: 1,
    });
    const allKpfsMapped: Record<string, string> = {};

    const userCache = newOperationCache<ChancenwerkUser>(async (id: string): Promise<Maybe<ChancenwerkUser>> => {
      return ChancenwerkUser.getByID<ChancenwerkUser>(id)({ _id: 1, vorname: 1, nachname: 1, email: 1 });
    });

    const partnerCache = newOperationCache<Partner>(async (id: string): Promise<Maybe<Partner>> => {
      return Partner.getByID<Partner>(id)({ kuerzel: 1, name: 1 });
    });

    const attendanceCache = newOperationCache<AttendanceOverviewResult>(
      (userID: string): Promise<Maybe<AttendanceOverviewResult>> =>
        AttendanceAPI.getOverview(userID)({ lastAttendance: 1, attendanceCount: 1 }),
    );

    const userActivityAnalyticCache = newOperationCache<ChancenUserActivityAnalytics>(
      (userID: string): Promise<Maybe<ChancenUserActivityAnalytics>> =>
        ChancenUserActivityAnalytics.retrieveAnalytic<ChancenUserActivityAnalytics>({ userID }),
    );

    const pageloadeventlogCache = newOperationCache<PageLoadEventLog[]>(
      async (userID: string): Promise<PageLoadEventLog[]> => {
        const col = await getCollection<typeof PageLoadEventLog>(PageLoadEventLog);
        const a = await col.find({ _class: PageLoadEventLog._class, userID }).toArray();
        return a.map(x => x as PageLoadEventLog);
      },
    );

    let relationshipsCached = false;
    const allRelationships: Record<string, string[]> = {};

    const ensureRelationshipsLoaded = async () => {
      if (relationshipsCached) return;
      const rels = await loadMongoData(
        SemanticRelationship,
        {
          predicateID: 'STUDENT',
        },
        {
          projection: { objectID: 1, subjectID: 1 },
        },
      );
      rels.forEach(rel => {
        if (!rel.subjectID) {
          return;
        }
        allRelationships[rel.subjectID] ??= [];
        allRelationships[rel.subjectID]?.push(rel.objectID ?? '');
      });
      relationshipsCached = true;
    };
    const ensureKPFsCached = async () => {
      if (kpfCached) return;
      const kpfs = await allKPFs;
      kpfs.forEach(kpf => {
        allKpfsMapped[kpf._id] = kpf.kuerzel;
      });
      kpfCached = true;
    };

    const relevantProducts = ['CHF', 'CAM'];
    const isIncludedInActivityMetricCache = newOperationCache<boolean>(async (userID: string): Promise<boolean> => {
      await ensureRelationshipsLoaded();
      await ensureKPFsCached();
      return !!allRelationships[userID]
        ?.map(id => allKpfsMapped[id])
        ?.find(k => k && relevantProducts.some(p => k.endsWith(p)));
    });

    super({
      delimiter: ';',
      className: Student._class,
      uniqueIdentifierFieldName: 'studentID',
      fieldMappings: [
        // Primitive fields
        new FieldMappingEntry({ output: 'studentID', input: 'ID' }, (v, data, i) => '4900' + v),
        new FieldMappingEntry({ output: 'schuelertyp', input: 'Schuelertyp' }),
        new FieldMappingEntry({ output: 'vorname', input: 'Vorname' }),
        new FieldMappingEntry({ output: 'nachname', input: 'Familienname' }),
        new FieldMappingEntry(
          { output: 'geburtstag', input: 'Person_Geb' },
          wrappedParseDate('Person_Geb'),
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'geschlecht', input: 'Person_Geschlecht' }),
        new FieldMappingEntry({ output: 'handynummer', input: 'Person_Handy' }),
        new FieldMappingEntry({ output: 'staatsangehoerigkeit', input: 'Herkunft' }, undefined, v => {
          let result = isoCountry.default.getCountry('de', v);
          if (!result) result = v;
          return result;
        }),
        new FieldMappingEntry({ output: 'email', input: 'Person_Mail' }),

        // Complex fields
        new FieldMappingEntry({ output: 'adresse.strasse', input: 'STR' }),
        new FieldMappingEntry({ output: 'adresse.hausnummer', input: 'HausNr' }),
        new FieldMappingEntry({ output: 'adresse.zipCity.zip', input: 'PLZ' }),
        new FieldMappingEntry({ output: 'adresse.zipCity.city', input: 'Stadt' }),
        new FieldMappingEntry({ output: 'bankInformation.kontoinhaber', input: 'Kontoinhaber' }),
        new FieldMappingEntry({ output: 'bankInformation.iban', input: 'IBAN' }),
        new FieldMappingEntry({ output: 'bankInformation.bic', input: 'BIC' }),
        new FieldMappingEntry({ output: 'bankInformation.kreditinstitut', input: 'Kreditinstitut' }),
        new FieldMappingEntry({ output: 'bankInformation.ibanFalsch', input: 'IBAN_Falsch' }),
        new FieldMappingEntry({
          output: 'bankInformation.vermerkeZahlungsverkehr',
          input: 'Vermerke_Zahlungsverkehr',
        }),
        new FieldMappingEntry({ output: 'bankInformation.debitorennummer', input: 'Debitorennummer' }),
        new FieldMappingEntry(
          { output: 'bankInformation.mandatsdatum', input: 'Mandatsdatum' },
          wrappedParseDate('Mandatsdatum'),
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'bankInformation.mandatsreferenz', input: 'Mandatsreferenz' }),
        new FieldMappingEntry({ output: 'bankInformation.zahlungsart', input: 'Zahlungsart' }),
        new FieldMappingEntry(
          { output: 'bankInformation.zahlungAusgesetztAb', input: 'Zahlung_ausgesetzt_ab' },
          wrappedParseDate('Zahlung_ausgesetzt_ab'),
          serializeDate(),
        ),
        new FieldMappingEntry(
          { output: 'bankInformation.zahlungAusgesetztBis', input: 'Zahlung_ausgesetzt_bis' },
          wrappedParseDate('Zahlung_ausgesetzt_bis'),
          serializeDate(),
        ),
        new FieldMappingEntry(
          {
            output: 'bankInformation.mitgliedsbeitrag',
            input: 'Abweichender_Mitgliedsbeitrag',
          },
          (v, data, i) => {
            if (v === undefined) return 20;

            const parsedVal = parseInt(v);
            if (isNaN(parsedVal)) {
              const errorMsg = `Invalider Abweichender Mitgliedsbeitrag: ${v} - bankInformation.mitgliedsbeitrag`;
              (data as any).importLogs = ((data as any).importLogs || '') + `${errorMsg}<br />`;
              return 20;
            }
            if (parsedVal === 0) return 0;
            return parsedVal;
          },
          (v, data, i) => {
            if (v === 0) return '0';
            else if (v) return v.toString();
            return '';
          },
        ),
        new FieldMappingEntry(
          {
            output: 'bankInformation.membershipFeeLowerThan20',
            input: 'Mitgliedsbeitrag_unter_20',
          },
          wrappedParseBoolean('Mitgliedsbeitrag_unter_20'),
          (v, data, i) => (v === undefined ? '' : v.toString()),
        ),

        // TODO we desperatly need a new export so this should not be hardcoded
        ...StudentConvertConfig._butToFieldMappings('buts', 'BuT SJ 23/24', 'BuT_SJ_23/24_'),

        new FieldMappingEntry(
          { output: 'roles', input: 'Schule' },
          undefined,
          async (v, data: PartialEntityData<Student>, i) => {
            // * We cache the relationships to avoid multiple queries to the database
            await ensureRelationshipsLoaded();

            const ids = allRelationships[data._id] ?? [];

            // * We cache the KPFs and create a map to avoid using find on the array
            await ensureKPFsCached();

            if (!ids?.length) {
              return '';
            }

            const kuerzels = new Set<string>();
            for (const id of ids) {
              const kuerzel = allKpfsMapped[id];
              if (kuerzel) {
                kuerzels.add(kuerzel);
              }
            }

            return Array.from(kuerzels).join(' ');
          },
        ), // need to created new product with school
        new FieldMappingEntry({ output: 'schoolInformation.klassenlehrer', input: 'Klassenlehrer' }),
        new FieldMappingEntry({ output: 'schoolInformation.zug', input: 'Zug' }),
        new FieldMappingEntry(
          { output: 'schoolInformation.jahrgangsstufe', input: 'Jahrgangsstufe' },
          (v, data, i, entity: PartialEntityData<Student>) => {
            if (!v) return undefined;
            const zug = Number(v);
            if (isNaN(zug)) {
              const errorMsg = `Invalide Jahrgangsstufe: '${v}' - Jahrgangsstufe `;
              entity.importLogs = (entity.importLogs || '') + `${errorMsg}<br />`;
              return undefined;
            }
            return zug;
          },
        ), // remove letters
        new FieldMappingEntry(
          { output: 'schoolInformation.abweichendeSchuleID', input: 'AbweichendeSchule' },
          undefined,
          async (id, data) => {
            if (!id) return '';

            const res = await partnerCache.get(id);
            return res?.toString();
          },
        ),
        new FieldMappingEntry({ output: 'legalGuardian1.vorname', input: 'Erzb_1_Vorname' }),
        new FieldMappingEntry({ output: 'legalGuardian1.nachname', input: 'Erzb_1_Familienname' }),
        new FieldMappingEntry(
          { output: 'legalGuardian1.staatsangehoerigkeit', input: 'Erzb_1_Herkunft' },
          undefined,
          v => {
            let result = isoCountry.default.getCountry('de', v);
            if (!result) result = v;
            return result;
          },
        ),
        new FieldMappingEntry({ output: 'legalGuardian1.beruf', input: 'Erzb_1_Beruf' }),
        new FieldMappingEntry({ output: 'legalGuardian1.handynummer', input: 'Erzb_1_Mobil' }),
        new FieldMappingEntry({ output: 'legalGuardian1.festnetznummer', input: 'Person_Telefon' }),
        new FieldMappingEntry({ output: 'legalGuardian1.email', input: 'Erzb_1_Mail' }),
        new FieldMappingEntry({ output: 'legalGuardian1.geschlecht', input: 'Erzb_1_Sex' }),
        new FieldMappingEntry({ output: 'legalGuardian2.vorname', input: 'Erzb_2_Vorname' }),
        new FieldMappingEntry({ output: 'legalGuardian2.nachname', input: 'Erzb_2_Familienname' }),
        new FieldMappingEntry(
          { output: 'legalGuardian2.staatsangehoerigkeit', input: 'Erzb_2_Herkunft' },
          undefined,
          v => {
            let result = isoCountry.default.getCountry('de', v);
            if (!result) result = v;
            return result;
          },
        ),
        new FieldMappingEntry({ output: 'legalGuardian2.beruf', input: 'Erzb_2_Beruf' }),
        new FieldMappingEntry({ output: 'legalGuardian2.handynummer', input: 'Erzb_2_Mobil' }),
        new FieldMappingEntry({ output: 'legalGuardian2.email', input: 'Erzb_2_Mail' }),
        new FieldMappingEntry({ output: 'legalGuardian2.geschlecht', input: 'Erzb_2_Sex' }),
        new FieldMappingEntry(
          { output: 'consent.einverstaendnisBild', input: 'Einverstaendnis_Bild' },
          wrappedParseBoolean('Einverstaendnis_Bild'),
        ),
        new FieldMappingEntry(
          {
            output: 'consent.einverstaendnisEntbindungVonVerschwiegenheit',
            input: 'Einverstaendnis_Entbindung_Von_Verschwiegenheit',
          },
          wrappedParseBoolean('Einverstaendnis_Entbindung_Von_Verschwiegenheit'),
        ),
        new FieldMappingEntry(
          { output: 'consent.einverstaendnisEvaluation', input: 'Einverstaendnis_Evaluation' },
          wrappedParseBoolean('Einverstaendnis_Evaluation'),
        ),
        new FieldMappingEntry(
          { output: 'kuendigung.austrittsdatum', input: 'Austrittsdatum' },
          wrappedParseDate('Austrittsdatum'),
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'kuendigung.kuendigungGrund', input: 'Kuendigung_Grund' }),
        new FieldMappingEntry({ output: 'kuendigung.kuendigungGrundSonst', input: 'Kuendigung_Grund_Sonst' }),
        new FieldMappingEntry({ output: 'kuendigung.kuendigungArt', input: 'Kuendigung_art' }),
        new FieldMappingEntry({ output: 'kuendigung.kuendigungDurch', input: 'Kuendigung_durch' }),
        new FieldMappingEntry({ output: null, input: 'Kuendigung_Grund' }, undefined, (v, data, i) => {
          const student = data as Student;
          return student.kuendigung?.kuendigungGrund ?? '';
        }),
        new FieldMappingEntry({ output: null, input: 'Kuendigung_Grund_Sonst' }, undefined, (v, data, i) => {
          const student = data as Student;
          return student.kuendigung?.kuendigungGrundSonst ?? '';
        }),
        new FieldMappingEntry({ output: 'funding.teilleistungsschwaechen', input: 'Teilleistungsschwaechen' }),
        new FieldMappingEntry({
          output: 'funding.teilleistungsschwaechenSonst',
          input: 'Teilleistungsschwaechen_Sonst',
        }),
        new FieldMappingEntry({ output: 'funding.vermerkeSonst', input: 'Vermerke_Sonst' }),
        new FieldMappingEntry(
          { output: 'buts.0.sonstigeFaecherTmp', input: 'Foerderbedarf_Sonst' },
          (v, data, i) => v || '',
        ),
        new FieldMappingEntry(
          { output: 'funding.foerderbedarfLautEltern', input: 'Foerderbedarf_lt_Eltern' },
          (v: string | undefined, data, i, entity) => {
            if (!v) return [];
            const faecher = v.split(' ');
            const student = entity as Student;

            const otherFaecher = faecher
              .filter(f => !Object.values(SchoolSubject).includes(f as SchoolSubject))
              .join(', ');
            if (otherFaecher.length > 0) {
              const errorMsg = `Folgende Fächer konntent nicht importiert werden: "${otherFaecher}" - Foerderbedarf_lt_Eltern`;
              student.importLogs = (student.importLogs || '') + `${errorMsg}<br />`;
            }

            return faecher.filter(fach => Object.values(SchoolSubject).includes(fach as SchoolSubject));
          },
        ),
        new FieldMappingEntry(
          { output: 'joinInformation.eintrittsdatum', input: 'Eintrittsdatum' },
          wrappedParseDate('Eintrittsdatum'),
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'bildungsKarteNummer', input: 'Bildungskarten-Nummer' }),
        new FieldMappingEntry(
          { output: 'lastCampusLogin', input: 'letzer Campus-Login' },
          wrappedParseDate('letzer Campus-Login'),
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'joinInformation.registermethode', input: 'Anmeldemethode' }),
        new FieldMappingEntry(
          { output: 'joinInformation.sendedPartTwoTillFive', input: 'Versand von Unterschriftenzettel an Eltern' },
          wrappedParseDate('Versand von Unterschriftenzettel an Eltern'),
          serializeDate(),
        ),
        new FieldMappingEntry({ output: 'zustellbesonderheiten', input: 'Anderer_Name_auf_Briefkasten' }),
        new FieldMappingEntry({ output: '_id', input: 'Digitale_Kurse_gewählt' }, undefined, async (text, data, i) => {
          const digitalCoursesChoosen = (await CourseDigital.getCourseForUser(
            text,
            new CourseFilter({ courseType: CourseDigital._class }),
          )({
            subject: 1,
            weekday: 1,
            startTime: 1,
            jahrgangsstufe: 1,
          } as BaseQLSelectionSet<CourseDigital>)) as CourseDigital[];

          return digitalCoursesChoosen
            .map(c =>
              `${c.subject ?? ''} ${c.weekday ?? ''} ${c.startTime?.toFormat('HH:mm') ?? ''} ${
                c.jahrgangsstufe ?? ''
              }`.trim(),
            )
            .join(', ');
        }),
        new FieldMappingEntry({ output: 'preferedFirstSubject', input: 'Fach_Erstwunsch' }),
        new FieldMappingEntry({ output: 'preferedSecondSubject', input: 'Fach_Zweitwunsch' }),
        new FieldMappingEntry({ output: 'preferedThirdSubject', input: 'Fach_Drittwunsch' }),
        new FieldMappingEntry({ output: 'daysAvailable', input: 'Bedarfsabfrage' }, undefined, (value, data, i) => {
          const days = value as DayAvailable[];
          return days.map(d => `${d.day} (${d.timesAvailable.map(t => t.toFormat('HH:mm')).join(', ')})`).join(', ');
        }),
        new FieldMappingEntry(
          { output: 'legalGuardianContactMethods', input: 'Bevorzugte Kontaktwege' },
          undefined,
          (value, data, i) => {
            return value.join(', ');
          },
        ),
        new FieldMappingEntry(
          { output: 'consent.gotSignedInfopostBack', input: 'Verwaltung_Infobrief_zurueckerhalten' },
          wrappedParseDate('Verwaltung_Infobrief_zurueckerhalten'),
          serializeDate(),
        ),
        new FieldMappingEntry(
          { output: 'bankInformation.wishesProofOfPayment', input: 'Nachweis_Beitragszahlungen_gewuenscht' },
          wrappedParseBoolean('Nachweis_Beitragszahlungen_gewuenscht'),
        ),
        new FieldMappingEntry({ output: 'guestAccount', input: 'ist_Gast' }, wrappedParseBoolean('ist_Gast')),

        ...StudentConvertConfig._butToFieldMappings('buts', 'BuT SJ 24/25', 'BuT_SJ_24/25_'),

        new FieldMappingEntry({ output: '_id', input: 'Dokumentation' }, undefined, async (value, data, i) => {
          const docs = await DocumentationAPI.getStudentContactNotes(value as string)({
            contactUserID: 1,
            date: 1,
            note: 1,
            method: 1,
          });

          for (const doc of docs) {
            if (!doc.contactUserID) {
              continue;
            }
            await userCache.get(doc.contactUserID);
          }

          return `"${docs
            .map(
              d =>
                `${d.date?.toFormat('dd.MM.yyyy')},${userCache.getStored(d.contactUserID)?.vorname} ${
                  userCache.getStored(d.contactUserID)?.nachname
                },${d.method},${cleanHTMLFieldForExport(d.note)}`,
            )
            .join(';')}"`;
        }),
        new FieldMappingEntry(
          { output: null, input: 'Letzter_Aufruf_CHANCENcampus' },
          undefined,
          async (value, data: PartialEntityData<Student>, i) => {
            if (!data._id) return '-';
            const isIncludedInActivityMetric = await isIncludedInActivityMetricCache.get(data._id);
            if (!isIncludedInActivityMetric) return '-';
            const analytic = await userActivityAnalyticCache.get(data._id);
            if (!analytic) {
              const logs = await pageloadeventlogCache.get(data._id);
              const latest = logs?.map(a => a.createdAt).reduce((acc, elm) => (acc > elm ? acc : elm), new Date(0));
              if (!latest) return '-';
              else {
                return DateTime.fromJSDate(latest).toFormat('dd.MM.yyyy') ?? '-';
              }
            } else {
              const sl = analytic.serviceLogins.find(s => s.service.toLowerCase() === 'chancencampus');
              const dates = sl?.loginDates;
              const latest = dates?.reduce((acc, elm) => (acc > elm ? acc : elm), new Date(0));
              if (!latest) return '-';
              else {
                return DateTime.fromJSDate(latest).toFormat('dd.MM.yyyy') ?? '-';
              }
            }
          },
        ),
        new FieldMappingEntry(
          { output: null, input: 'Anzahl_Aufrufe_CHANCENcampus' },
          undefined,
          async (value, data: PartialEntityData<Student>, i) => {
            if (!data._id) return '0';
            const isIncludedInActivityMetric = await isIncludedInActivityMetricCache.get(data._id);
            if (!isIncludedInActivityMetric) return '0';
            const analytic = await userActivityAnalyticCache.get(data._id);
            if (!analytic) {
              const logs = await pageloadeventlogCache.get(data._id);
              const length = logs?.length ?? 0;
              return length.toString();
            } else {
              const sl = analytic.serviceLogins.find(s => s.service.toLowerCase() === 'chancencampus');
              const dates = sl?.loginDates;
              const length = dates?.length ?? 0;
              return length.toString();
            }
          },
        ),
        new FieldMappingEntry(
          { output: null, input: 'Letzte_Anwesenheit' },
          undefined,
          async (value, data: PartialEntityData<Student>, i) => {
            if (!data._id) return '';
            const overview = await attendanceCache.get(data._id);
            return overview?.lastAttendance?.toFormat('dd.MM.yyyy') ?? '';
          },
        ),
        new FieldMappingEntry(
          { output: null, input: 'Anzahl_Anwesenheiten' },
          undefined,
          async (value, data: PartialEntityData<Student>, i) => {
            if (!data._id) return '0';
            const overview = await attendanceCache.get(data._id);
            return overview?.attendanceCount?.toString() ?? '0';
          },
        ),
      ],
      foreignKeys: [],
    });
  }
}

const wrappedParseDate = (header: string) => (value: string | undefined, data: Object, idx, entity) => {
  try {
    return parseDate(value);
  } catch (e) {
    const error = e as Error;
    const errorMsg = `${error.message}: "${value}" - ${header}`;
    entity.importLogs = (entity.importLogs || '') + `${errorMsg}<br />`;
    return null;
  }
};

const wrappedParseBoolean = (header: string) => (value: string | undefined, data: Object, idx, entity) => {
  try {
    return parseBoolean(false)(value);
  } catch (e) {
    const error = e as Error;
    const errorMsg = `${error.message}: "${value}" - ${header}`;
    entity.importLogs = (entity.importLogs || '') + `${errorMsg}<br />`;
    return null;
  }
};
